import { Component, OnInit, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EmptyStateComponent, EnterpriseLabels, getEnterpriseLabels, PaginationComponent, SecurityDomain, SsoUserDetailsDto, UserRoleEnum, UserSearchService } from 'lib';
import { LazyLoadEvent } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { CommonModule } from '@angular/common';
import { CheckboxChangeEvent } from 'primeng/checkbox';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute } from '@angular/router';
import { EnterpriseAdminService } from '../../services/enterprise-admin.service';
import { debounceTime, Subject, take } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CreateUniversalIdComponent } from '../create-universal-id/create-universal-id.component';
import { EnterpriseUserImpersonationService } from '../../services/enterprise-user-impersonation.service';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { EnterpriseSharedService } from '../../services/enterprise-shared.service';
import { EnterprisePrimaryAdminService } from '../../services/enterprise-primary-admin.service';
import { EnterpriseLabelsFetchService } from '../../services/enterprise-labels-fetch.service';

@Component({
    selector: 'mmoga-admin-users-table',
    standalone: true,
    imports: [
        EmptyStateComponent,
        PaginationComponent,
        TableModule,
        FormsModule,
        CommonModule,
        FaIconComponent
    ],
    templateUrl: './admin-users-table.component.html',
    styleUrl: './admin-users-table.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class AdminUsersTableComponent implements OnInit {
    private enterpriseAdminService = inject(EnterpriseAdminService);
    private enterpriseUserImpersonationService = inject(EnterpriseUserImpersonationService);
    private userSearchService = inject(UserSearchService);
    private toastrService = inject(ToastrService);
    private activatedRoute = inject(ActivatedRoute);
    private modalService = inject(NgbModal);
    private readonly enterpriseSharedService = inject(EnterpriseSharedService);
    private readonly enterprisePrimaryAdminService = inject(EnterprisePrimaryAdminService);
    private readonly enterpriseLabelsFetchService = inject(EnterpriseLabelsFetchService);

    currentEnterpriseId: number | null = null;
    tableData!: SsoUserDetailsDto[];
    currentPage = 1;
    itemsPerPage = 10;
    totalItems = 0;
    minSearchTermLength = 3;
    searchTerm = '';
    sortBy = 'username';
    sortOrder = 'ASC';
    faLock = faLock;

    onlyShowOrgAdmins = false;

    @ViewChild('pagination') pagination!: PaginationComponent;

    private previousSearchTerm = '';
    private searchSubject: Subject<string> = new Subject<string>();
    enterpriseLabels: EnterpriseLabels = getEnterpriseLabels(SecurityDomain.MODMED);

    ngOnInit(): void {
        this.enterpriseLabelsFetchService.getEnterpriseLabels().then(
            labels => this.enterpriseLabels = labels
        );
        this.activatedRoute.parent?.params.subscribe(params => {
            this.currentEnterpriseId = params['id'] ? +params['id'] : null;
            this.loadUsers();
        });

        this.searchSubject.pipe(
            debounceTime(300)
        ).subscribe(searchTerm => {
            if (searchTerm.length >= this.minSearchTermLength || searchTerm === '') {
                this.loadUsers(); // Reload data when search term changes
            }
        });
    }

    onSearchTermChange(): void {
        if (this.searchTerm !== this.previousSearchTerm) {
            this.previousSearchTerm = this.searchTerm;
            this.searchSubject.next(this.searchTerm);
        }
    }

    onAdminFilterChange(): void {
        this.currentPage = 1;
        this.loadUsers();
    }

    onCreateUniversalIdClick(): void {
        const modalRef = this.modalService.open(CreateUniversalIdComponent, { size: 'xl', windowClass: 'nb-layout' });
        modalRef.componentInstance.currentEnterpriseId = this.currentEnterpriseId;
        modalRef.result.finally(() => {
            this.loadUsers();
        });
    }

    onImpersonateOrgAdminClick(user: SsoUserDetailsDto): void {
        this.impersonateOrgAdmin(user);
    }

    onImpersonateOrganizationUserClick(user: SsoUserDetailsDto): void {
        this.impersonateOrganizationUser(user);
    }

    loadUsers(): void {
        if (this.currentEnterpriseId) {
            this.userSearchService.getUsersByEnterpriseId(
                this.currentEnterpriseId,
                this.searchTerm,
                this.currentPage - 1,
                this.itemsPerPage,
                this.sortBy,
                this.sortOrder,
                this.onlyShowOrgAdmins ? true : null)
                .subscribe({
                    next: (data) => {
                        this.tableData = data.content;
                        this.totalItems = data.totalElements;
                        this.pagination = new PaginationComponent();
                        this.pagination.totalItems = this.totalItems;
                        if (data.content.length === 0) {
                            this.currentPage = 1;
                        }
                        this.pagination?.ngOnInit();
                    }
                });
        }
    }

    customSort(event: LazyLoadEvent): void {
        this.sortBy = event.sortField ? event.sortField : 'username';
        this.sortOrder = event.sortOrder === 1 ? 'ASC' : 'DESC';
        this.loadUsers();
    }

    onSelectItemsPerPage(itemsPerPage: number): void {
        this.itemsPerPage = itemsPerPage;
    }

    onChangeCurrentPage(currentPage: number): void {
        this.currentPage = currentPage;
        this.loadUsers();
    }

    public toggleGroupAdminUser(user: SsoUserDetailsDto, event: Event): void {
        if (event.target as CheckboxChangeEvent) {
            const checkedEvent = event.target as CheckboxChangeEvent;
            if (checkedEvent.checked) {
                this.addAdminRole(user);
            } else {
                this.removeAdminRole(user);
            }
        }
    }

    addAdminRole(user: SsoUserDetailsDto) {
        this.enterpriseAdminService.addAdminRole(user.id).subscribe({
            next: () => {
                this.toastrService.success('User Admin role set', 'Accounts');
                this.addPrimaryAdminRole(user.id);
            },
            error: () => {
                this.toastrService.error('There was an error when trying to set user Admin role', 'Accounts');
            }
        });
    }

    addPrimaryAdminRole(userId: string) {
        this.enterprisePrimaryAdminService.addRole(userId).subscribe({
            error: () => {
                this.toastrService.error('There was an error when trying to set user as Primary Admin', 'Accounts');
            }
        });
    }

    removePrimaryAdminRole(userId: string) {
        this.enterprisePrimaryAdminService.removeRole(userId).subscribe({
            error: () => {
                this.toastrService.error('There was an error when trying to remove Primary Admin role', 'Accounts');
            }
        });
    }

    hasEnterpriseAdminRole(ssoUserDetails: SsoUserDetailsDto) {
        return ssoUserDetails.roleList?.includes(UserRoleEnum.ENTERPRISE_ADMIN);
    }

    private removeAdminRole(user: SsoUserDetailsDto) {
        this.enterpriseAdminService.removeAdminRole(user.id).subscribe({
            next: () => {
                this.toastrService.success('User Admin role removed', 'Accounts');
                this.removePrimaryAdminRole(user.id);
            },
            error: () => {
                this.toastrService.error('There was an error when trying to remove user Admin role', 'Accounts');
            }
        });
    }

    private impersonateOrgAdmin(user: SsoUserDetailsDto): void {
        this.enterpriseSharedService.enterprise$
            .pipe(take(1))
            .subscribe({
                next: (enterprise) => {
                    if (enterprise) {
                        window.open(this.enterpriseUserImpersonationService.getOrgAdminImpersonationUrl(user.id, enterprise), '_blank');
                    }
                },
                error: () => this.toastrService.error('Failed to retrieve enterprise.')
            });
    }

    private impersonateOrganizationUser(user: SsoUserDetailsDto): void {
        const impersonationUrl = user.defaultLinkedAccount?.organizationDto?.impersonationUrl;
        if (!impersonationUrl) {
            this.toastrService.error('No linked organization found for this user.');
            return;
        }
        const firmUserId = user.defaultLinkedAccount?.userId;
        window.open(this.enterpriseUserImpersonationService.getOrganizationUserImpersonationUrl(user.id, impersonationUrl, firmUserId), '_blank');
    }
}
